import { IMAGE_SERVER_URL } from '../config/app'
import { waitForImageLoading } from './delayed-image-loading'

export interface GibTokenMetadata {
  chainId: number
  address: string
  name: string
  symbol: string
  decimals: number
  logoURI: string
}

export interface GibApiResponse {
  name: string
  timestamp: string
  version: { major: number; minor: number; patch: number }
  tokens: GibTokenMetadata[]
}

class GibMetadataService {
  private static instance: GibMetadataService
  private metadataMap: Map<string, GibTokenMetadata> = new Map()
  private loaded = false
  private loadingPromise: Promise<void> | null = null

  private constructor() {}

  static getInstance(): GibMetadataService {
    if (!GibMetadataService.instance) {
      GibMetadataService.instance = new GibMetadataService()
    }
    return GibMetadataService.instance
  }

  async loadMetadata(): Promise<void> {
    if (this.loaded) {
      return
    }

    if (this.loadingPromise) {
      return this.loadingPromise
    }

    this.loadingPromise = this.fetchAndCacheMetadata()
    return this.loadingPromise
  }

  private async fetchAndCacheMetadata(): Promise<void> {
    try {
      // Wait for image loading to be enabled (delays until after critical path)
      await waitForImageLoading()

      console.log('🌐 Fetching token metadata from Gib.show...')

      const response = await fetch(`${IMAGE_SERVER_URL}/list/etherscan/top-tokens`)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data: GibApiResponse = await response.json()
      console.log(`✅ Loaded ${data.tokens.length} tokens from Gib.show`)

      // Create lookup map with normalized addresses
      this.metadataMap = new Map()
      data.tokens.forEach(token => {
        const key = `${token.chainId}:${token.address.toLowerCase()}`
        this.metadataMap.set(key, token)
      })

      this.loaded = true
      console.log(`📋 Cached ${this.metadataMap.size} token metadata entries`)
    } catch (error) {
      console.error('❌ Failed to fetch Gib.show metadata:', error)
      // Don't set loaded to true on error, so we can retry
      this.loadingPromise = null
      throw error
    }
  }

  getTokenMetadata(chainId: number, address: string): GibTokenMetadata | null {
    if (!this.loaded) {
      console.warn('Gib metadata not loaded yet. Call loadMetadata() first.')
      return null
    }

    const key = `${chainId}:${address.toLowerCase()}`
    return this.metadataMap.get(key) || null
  }

  hasTokenMetadata(chainId: number, address: string): boolean {
    if (!this.loaded) {
      return false
    }

    const key = `${chainId}:${address.toLowerCase()}`
    return this.metadataMap.has(key)
  }

  getAllMetadata(): GibTokenMetadata[] {
    return Array.from(this.metadataMap.values())
  }

  isLoaded(): boolean {
    return this.loaded
  }

  // Get stats for debugging
  getStats() {
    const tokensByChain = new Map<number, number>()
    this.metadataMap.forEach(token => {
      tokensByChain.set(token.chainId, (tokensByChain.get(token.chainId) || 0) + 1)
    })

    return {
      totalTokens: this.metadataMap.size,
      tokensByChain: Object.fromEntries(tokensByChain),
      loaded: this.loaded,
    }
  }
}

export const gibMetadataService = GibMetadataService.getInstance()
